/**
 * Rimowa specific configuration.
 *
 * This will be merged with `src/config/index.ts`
 */

//import { isProd } from "../../utils/stage";
import { get } from "../../utils/helpers";
import customerDataForm from "./forms/customerData";
import customerConsentForm from "./forms/customerConsent";
import customerAdditionalInfo from "./forms/customerAdditionalInfo";
import { toDDCCustomer } from "./transforms";
import debug from "../../utils/debug";
const stage: string = process.env.REACT_APP_STAGE || "local";

const license_dqe_prod = "X5UQno-g~V4pC4alK_5xRlF4zc";
const license_dqe_preprod = "Y55-n2AP3_CwibTpB397WH1vGa";

const preprod_DQE = [
  `https://preprod3.dqe-software.com/SINGLEV2/?Pays={{country}}&Licence=${license_dqe_preprod}&Adresse={{search}}`,
  `https://preprod3.dqe-software.com/DQEEMAILLOOKUP/?Email={{email}}&Licence=${license_dqe_preprod}`,
  `https://preprod3.dqe-software.com/TEL/?Tel={!phone}&Licence=${license_dqe_preprod}&Pays={!currentCountry}`
];

const prod_DQE = [
  `https://prod3.dqe-software.com/SINGLEV2/?Pays={{country}}&Licence=${license_dqe_prod}&Adresse={{search}}`,
  `https://prod3.dqe-software.com/DQEEMAILLOOKUP/?Email={{email}}&Licence=${license_dqe_prod}`,
  `https://prod3.dqe-software.com/TEL/?Tel={!phone}&Licence=${license_dqe_prod}&Pays={!currentCountry}`
];

const [
  OIDC_BASE_URL,
  OIDC_CLIENT_ID,
  API_BASE_URL,
  DQE_ENDPOINT_ADDRESS,
  DQE_ENDPOINT_EMAIL,
  DQE_ENDPOINT_VALIDATION_PHONE
] =
  stage === "local"
    ? [
        "https://rimowa.oktapreview.com/oauth2/default",
        "0oa6a2auiaiIHyie10x7",
        "http://localhost:",
        ...preprod_DQE
      ]
    : stage === "dev"
    ? [
        "https://rimowa.oktapreview.com/oauth2/default",
        "0oa6a2auiaiIHyie10x7",
        "https://apis.dev.rimowa.clienteling.lvmhda.com",
        ...preprod_DQE
      ]
    : stage === "staging"
    ? [
        "https://rimowa.oktapreview.com/oauth2/default",
        "0oa6f93fx25GA0uva0x7",
        "https://apis.staging.rimowa.clienteling.lvmhda.com",
        ...preprod_DQE
      ]
    : [
        "https://rimowa.okta.com/oauth2/aus3oaragqJLaRSsc417",
        "0oa3oamdvbVKvXkJv417",
        "https://apis.rimowa.clienteling.lvmhda.com",
        ...prod_DQE
      ];

const SUPPORTED_LANGUAGES = [
  "cs",
  "de",
  "en-GB",
  "en-US",
  "es",
  "fr",
  "it",
  "nl",
  "pt",
  "ar",
  "ja",
  "ko",
  "th",
  "zh-Hans",
  "zh-Hant"
];
debug("env:", stage);
debug("baseUrl:", OIDC_BASE_URL);

export default {
  id: "rimowa",
  services: {
    oidc: {
      baseUrl: OIDC_BASE_URL,
      loginRedirectEndpoint: "/v1/authorize",
      scope: "openid offline_access",
      getTokenEndpoint: "/v1/token",
      logoutRedirectEndpoint: "/v1/logout",
      clientId: OIDC_CLIENT_ID,
      codeChallenge: "HPyLLwWqLbWJV0Ks7fArAD1TEGtV6JI2P8aG1EaVdjY",
      codeChallengeMethod: "S256",
      codeVerifier:
        "swbBEq~75oJQVAdgJEND6n7HNf5seQJIOuqwNE8qt33JOIbAYzsqOY.YmV_~1l_.nViF4f0xSiy87IsMh52zkHXwigneHj5FP2wcgUC0te~imSlRXsYrf4j1fH9_MwqJ"
    },
    api: {
      baseUrl: API_BASE_URL,
      getConfigEndpoint:
        stage !== "local"
          ? "/ddc/v1/ddcconfig"
          : `${API_BASE_URL}3003/ddcconfig`,
      getStoresEndpoint:
        stage !== "local" ? "/ddc/v1/stores" : `${API_BASE_URL}3003/stores`,
      verifyAdvisorEndpoint:
        stage !== "local"
          ? "/advisors/v1/advisors"
          : `${API_BASE_URL}3002/advisors`,
      getTitlesEndpoint:
        stage !== "local" ? "/ddc/v1/titles" : `${API_BASE_URL}3003/titles`,
      getNationalitiesEndpoint:
        stage !== "local"
          ? "/ddc/v1/nationalities"
          : `${API_BASE_URL}3003/nationalities`,
      getCountriesEndpoint:
        stage !== "local"
          ? "/ddc/v1/countries"
          : `${API_BASE_URL}3003/countries`,
      translationsEndpoint:
        stage !== "local"
          ? "/ddc/v1/translations/{{lng}}/{{ns}}"
          : `${API_BASE_URL}/translations{{lng}}/{{ns}}`,
      customerEndpoint:
        stage !== "local"
          ? "/clients/v1/clients"
          : `${API_BASE_URL}3001/clients`,
      regionsEndpoint:
        stage !== "local" ? "/ddc/v1/regions" : `${API_BASE_URL}3003/regions`,
      dqeEmailEndpoint: DQE_ENDPOINT_EMAIL,
      dqeAddressesEndpoint: DQE_ENDPOINT_ADDRESS
    }
  },
  languages: {
    supportedLanguages: [
      "en-US",
      "en-GB",
      "cs",
      "de",
      "es",
      "fr",
      "it",
      "nl",
      "pt",
      "ar",
      "ja",
      "ko",
      "th",
      "zh-Hant",
      "zh-Hans"
    ],
    backendCodes: {
      "en-US": "us",
      "en-GB": "gb",
      "en-SG": "en",
      "zh-Hant": "zh",
      "zh-Hans": "zh",
      cs: "cz",
      ja: "jp"
    }
  },
  // Zone config should follow what's been stipulated here:
  // https://docs.google.com/spreadsheets/d/1qY7SVNbzNy0urILrM2KOW_2VCGrDH-ZbatYOSxQqg6Q/edit?pli=1#gid=18956792
  zones: {
    de: {
      languages: ["de", "en"],
      supportedLanguages: SUPPORTED_LANGUAGES
    },
    gb: {
      languages: ["en"],
      supportedLanguages: SUPPORTED_LANGUAGES
    },
    it: {
      languages: ["it"],
      supportedLanguages: SUPPORTED_LANGUAGES
    },
    fr: {
      languages: ["fr"],
      supportedLanguages: SUPPORTED_LANGUAGES
    },
    ch: {
      languages: ["fr"],
      supportedLanguages: SUPPORTED_LANGUAGES
    },
    es: {
      languages: ["es"],
      supportedLanguages: SUPPORTED_LANGUAGES
    },
    nl: {
      languages: ["nl"],
      supportedLanguages: SUPPORTED_LANGUAGES
    },
    cz: {
      languages: ["cs"],
      supportedLanguages: SUPPORTED_LANGUAGES
    },
    hk: {
      languages: ["zh-Hant", "zh-Hans"],
      supportedLanguages: SUPPORTED_LANGUAGES
    },
    mo: {
      languages: ["zh-Hant", "zh-Hans"],
      supportedLanguages: SUPPORTED_LANGUAGES
    },
    cn: {
      languages: ["zh-Hans", "zh-Hant"],
      supportedLanguages: SUPPORTED_LANGUAGES
    },
    jp: {
      languages: ["ja"],
      supportedLanguages: SUPPORTED_LANGUAGES
    },
    kr: {
      languages: ["ko"],
      supportedLanguages: SUPPORTED_LANGUAGES
    },
    sg: {
      languages: ["en", "zh-Hans"],
      supportedLanguages: SUPPORTED_LANGUAGES
    },
    us: {
      languages: ["en-US", "es"],
      supportedLanguages: SUPPORTED_LANGUAGES
    },
    ca: {
      languages: ["en", "en-US", "fr"],
      supportedLanguages: SUPPORTED_LANGUAGES
    },
    br: {
      languages: [
        // "pt-BR",
        "en-US",
        "en"
      ],
      supportedLanguages: SUPPORTED_LANGUAGES
    },
    ae: {
      languages: ["ar", "en"],
      supportedLanguages: SUPPORTED_LANGUAGES
    },
    tw: {
      languages: ["zh-Hant", "zh-Hans"],
      supportedLanguages: SUPPORTED_LANGUAGES
    },
    au: {
      languages: ["en-GB"],
      supportedLanguages: SUPPORTED_LANGUAGES
    },
    my: {
      languages: ["en"],
      supportedLanguages: SUPPORTED_LANGUAGES
    },
    at: {
      languages: ["en"],
      supportedLanguages: SUPPORTED_LANGUAGES
    },
    th: {
      languages: ["th"],
      supportedLanguages: SUPPORTED_LANGUAGES
    },
    be: {
      languages: ["fr"],
      supportedLanguages: SUPPORTED_LANGUAGES
    }
  },
  customise: {
    termAndConditionInOnePlace: true,
    setupPage: {
      filterStoresByZone: true
    },
    header: {
      showLanguage: true
    },
    footer: {
      showLanguage: false
    },
    languageSwitcher: {
      showOnlyZoneLanguages: false
    },
    fields: {
      showOptionalLabel: true,
      markRequiredWithSymbol: false,
      customFields: ["CpfCnpjCode", "UserDefinedTable7Value"],
      showRequiredLabel: true
    },
    verifySalesPerson: {
      enable: stage !== "local" ? true : false
    },
    customer: {
      DQEValidationPhone: DQE_ENDPOINT_VALIDATION_PHONE,
      defaultLang: "emptyValue",
      enableCompleteSetup: true,
      enablePrefilled: false,
      enableAdditionalInfo: true,
      checkShowAdditionalInfo: (sharedState: {
        appState?: object;
        pageState?: object;
        formState?: object;
      }): boolean => {
        const language = get(sharedState, "pageState.language");
        const zone = get(sharedState, "appState.zone");
        return language === "ja" && zone === "jp";
      }
    }
  },
  forms: {
    schema: "form/customerFormDefinitions.json",
    fields: [
      "form/customerDataFields.json",
      "form/customerConsentFields.json",
      "form/customerThanks.json",
      "form/additionalInfoFields.json"
    ],
    customer: [customerDataForm, customerConsentForm, customerAdditionalInfo]
  },
  transforms: {
    customer: {
      write: toDDCCustomer
    }
  }
};
